import React, { useState, useEffect, useMemo, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { ParentNavigationLink } from "@/components/ui/parent-navigation-link"
import { navigateInParent } from "@/lib/app-messaging"
import { Button } from "@/components/ui/button"
import {
  Users,
  LayoutDashboard,
  Clock,
  ListTodo,
  FileText,
  Files,
  AlertCircle,
  ChevronRight,
  Receipt,
} from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import { statusOf } from "@/lib/api"
import { splitRefusal, recordRefusals, type RefusedSave, type WorkerRefusals } from "@/lib/utils/worker-save-errors"
import { useLanguage } from "@/contexts/language-context"
import { useFormatters } from "@/hooks/use-formatters"
import { useEngagementData } from "@/hooks/use-engagement-data"
import { useCodeValues } from "@/hooks/use-code-values"
import { useEngagementLookups } from "@/hooks/use-engagement-lookups"
import { useEngagementForms } from "@/hooks/use-engagement-forms"
import { usePageTitle } from "@/hooks/use-page-title"
import { useEngagementApi } from "@/hooks/use-engagement-api"
import { useUnsavedChanges } from "@/hooks/use-unsaved-changes"
import type { TransactionHeaderSummary, EngagementReferences } from "@/types/api/engagement"
import { ENGAGEMENT_STATUS } from "@/lib/constants/engagement-codes"
import type { NavigationMode } from "@/types/ui/engagement-view"
import { filterNonReversedTransactions, getMostRecentTransaction } from '@/lib/utils/transaction-utils'
import { apiToFormState, mergeCopiedReferences } from '@/lib/adapters/engagement-adapter'
import { DocumentsSection } from "@/components/engagement/DocumentsSection"
import WorkersSection from "@/components/engagement/WorkersSection"
import TasksSection from "@/components/engagement/TasksSection"
import { EngagementHeader } from "@/components/engagement/EngagementHeader"
import { TimesheetAndExpenseSection } from "@/components/engagement/TimesheetAndExpenseSection"
import { InvoiceSection } from '@/components/engagement/InvoiceSection'
import { CoreSection } from '@/components/engagement/CoreSection'
import { BillingSetupSection } from '@/components/engagement/BillingSetupSection'
import { FormStepper } from "@/components/navigation/FormStepper"
import { FormTabs } from "@/components/navigation/FormTabs"
import { EngagementToolbar } from "@/components/engagement/EngagementToolbar"
import { TransactionsContainer } from "@/components/engagement/TransactionsContainer"
import { DuplicateTransactionWarningDialog } from "@/components/engagement/DuplicateTransactionWarningDialog"
import { CommentDialog } from "@/components/engagement/CommentDialog"
import { CompleteEngagementDialog } from "@/components/engagement/CompleteEngagementDialog"
import { Tabs, TabsContent } from "@/components/ui/tabs"
import { EngagementNavigationProvider, useEngagementNavigation } from "@/contexts/engagement-navigation-context"
import { ENGAGEMENT_TABS } from "@/types/ui/engagement-view"
import type { MilestoneTransactionInfo } from '@/types/ui/milestone'
import { MILESTONE_STATUS } from '@/lib/constants/engagement-codes'
import { ENGAGEMENT_ACTIONS, getActionLabel, getActionLoadingLabel } from '@/lib/constants/engagement-actions'
import { useWatch } from 'react-hook-form'

// TODO: Move this into another file later, when we aren't working on this so much
function EngagementDetailsContent() {
  const { dictionary } = useLanguage()
  const { formatTime, formatDate } = useFormatters()
  const { toast } = useToast()
  const { activeTab, activeSection, sections, goToSection } = useEngagementNavigation()
  const { id: idParam, version: versionParam } = useParams<{ id: string; version?: string }>()
  const navigate = useNavigate()

  const requestedRecordId = idParam ? Number(idParam) : undefined
  const requestedVersion = versionParam ? Number(versionParam) : undefined

  // Format last saved timestamp - show time if today, date if not today
  const formatLastSaved = (date: Date): string => {
    const today = new Date()
    const isToday = date.toDateString() === today.toDateString()
    
    return isToday ? formatTime(date) : formatDate(date)
  }

  // Helper to create consistent engagement display ID
  const getEngagementDisplayId = () => {
    return version ? `${recordId}.${version}` : `${recordId}`
  }

  // Engagement data hook - loads data on page load
  const {
    data: { recordId, version, formState, allVersions, previousWorkerIds },
    capabilities,
    handlers,
    status: { loading: loadingEngagement, error: engagementError, errorIsAccessOrMissing, isHandlingAction, currentAction, lastSaved }
  } = useEngagementData(requestedRecordId, requestedVersion)

  // Code values hook - loads static code values on app boot
  const { isReady: codeValuesReady } = useCodeValues()

  // Engagement lookups hook - loads engagement-specific lookup data
  const { loaded: engagementLookupsReady } = useEngagementLookups()

  // Engagement API hook for fetching transactions
  const engagementApi = useEngagementApi()

  // State for duplicate transaction dialog
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false)
  const [duplicateTransactions, setDuplicateTransactions] = useState<TransactionHeaderSummary[]>([])
  const [pendingMilestoneGeneration, setPendingMilestoneGeneration] = useState<{ reference: string; amount: number } | null>(null)

  // State for milestone transaction info (for button labels and last generated info)
  const [milestoneTransactionInfo, setMilestoneTransactionInfo] = useState<Record<string, MilestoneTransactionInfo>>({})

  // State to track which milestone is currently generating
  const [generatingMilestoneRef, setGeneratingMilestoneRef] = useState<string | null>(null)

  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false)

  // State for comment dialog
  const [showCommentDialog, setShowCommentDialog] = useState(false)
  const [isSubmittingComment, setIsSubmittingComment] = useState(false)
  const [showCompleteDialog, setShowCompleteDialog] = useState(false)

  const isDraft = useMemo(() => formState?.actionContext.engagement.status 
    ? formState.actionContext.engagement.status === ENGAGEMENT_STATUS.DRAFT || formState.actionContext.engagement.status === ENGAGEMENT_STATUS.ACCOUNTING_DRAFT
    : false, [formState]
  )

  // Determine if form should be readonly
  const isFormReadonly = isHandlingAction || !capabilities.canSave
  
  // Determine navigation mode based on engagement state
  const navigationMode: NavigationMode = useMemo(() => {
    return version === 1 && isDraft ? 'setup' : 'existing'
  }, [version, isDraft])
  
  // Determine if this is a change request (version > 1)
  const isChangeRequest = version > 1

  // Use engagement forms hook - manages all form state and logic
  const {
    forms: { coreForm, billingForm, workersForm, timesheetAndExpenseForm, invoiceForm, milestonesForm, revenueRecognitionForm, tasksForm },
    handlers: { 
      handleMilestoneAdd, 
      handleMilestoneRemove, 
      handleWorkerAdd,
      handleWorkerEdit,
      handleWorkerRemove,
      handleTaskAdd,
      handleTaskEdit,
      handleTaskRemove,
      handleApproverAdd,
      handleApproverRemove,
      handleInvoiceNoteAdd,
      handleInvoiceNoteRemove,
      handleExpenseApproverAdd,
      handleExpenseApproverRemove
    },
    state: { completedSections, hiddenSections, allSectionsComplete, billingType, engagementType, isFormsPopulated, taskCoverage },
    actions: { buildCurrentFormState, submitForms, copyFromSource }
  } = useEngagementForms({ formState, isFormReadonly, isSetupMode: navigationMode === 'setup' })

  // GW-198: one-shot copy from the selected Original Engagement into the CR form
  const [isCopying, setIsCopying] = useState(false)

  // GW-323: the copied-from engagement's id->name/type map, kept until the next save refreshes the
  // current engagement's own references (see mergeCopiedReferences for the full reasoning).
  const [copiedReferences, setCopiedReferences] =
    useState<Pick<EngagementReferences, 'profiles' | 'organizations'> | null>(null)

  const references = useMemo(
    () => mergeCopiedReferences(capabilities.references, copiedReferences),
    [capabilities.references, copiedReferences]
  )

  // Drop the overlay when the page switches to a different engagement (the component stays mounted).
  useEffect(() => {
    setCopiedReferences(null)
  }, [recordId])

  const handleCopyFromOriginal = useCallback(async (): Promise<boolean> => {
    const sourceRecordId = coreForm.getValues('selectedOriginalEngagement')
    if (!sourceRecordId) return false
    try {
      setIsCopying(true)
      // 2-request load mirroring use-engagement-data.loadEngagementData, taking the latest version.
      const versions = await engagementApi.getEngagementVersions(sourceRecordId)
      if (!versions.data || versions.data.length === 0) {
        throw new Error('No versions found for the selected engagement')
      }
      // Copy from the engagement's LIVE version (Active, or PendingChange when a change is in flight) —
      // NOT the highest version number. An engagement with an unapproved change in progress has a
      // higher-numbered Draft/PendingReview version whose data is not yet approved; copying that into a
      // new engagement would propagate unapproved values. A Completed source has no such version and is
      // refused here; CoreSection hides the copy button for one.
      const byVersionDesc = [...versions.data].sort((a, b) => b.data.version - a.data.version)
      const liveVersion = byVersionDesc.find(
        v => v.data.status === ENGAGEMENT_STATUS.ACTIVE || v.data.status === ENGAGEMENT_STATUS.PENDING_CHANGE
      )
      if (!liveVersion) {
        throw new Error('The selected engagement has no active version to copy from')
      }
      const versionResponse = await engagementApi.getEngagementVersion(sourceRecordId, liveVersion.data.version)
      // The API redacts worker pay fields when the caller lacks pay-rate access. Copying in that state
      // would populate the new CR with 0/empty pay/bill rates and could save invalid payroll amounts,
      // so block the client-side copy entirely rather than silently zeroing out rates.
      if (versionResponse.canViewWorkerPayRates === false) {
        throw new Error('You need worker pay-rate access to copy from the selected engagement.')
      }
      const source = apiToFormState(versionResponse)
      copyFromSource(source)
      // GW-323: carry the source's id->name/type resolution across with the data it describes.
      setCopiedReferences({
        profiles: source.actionContext.references.profiles,
        organizations: source.actionContext.references.organizations,
      })
      // Success feedback is the inline confirmation strip in CoreSection (localized via the dictionary).
      // No success toast here — it would duplicate that message and bypass i18n.
      return true
    } catch (error) {
      toast({
        title: 'Copy Failed',
        description: error instanceof Error ? error.message : 'Could not copy from the selected engagement',
        variant: 'destructive',
      })
      return false
    } finally {
      setIsCopying(false)
    }
  }, [coreForm, engagementApi, copyFromSource, toast])

  // Unsaved changes tracking
  const { confirmNavigation } = useUnsavedChanges({
    forms: {
      coreForm,
      billingForm,
      workersForm,
      timesheetAndExpenseForm,
      invoiceForm,
      milestonesForm,
      revenueRecognitionForm,
      tasksForm
    }
  })

  // Watch core form values that children need to react to
  // Pattern: Parent watches and passes as props (not getValues) so children re-render when values change
  // This is important when children use these values in hooks, useMemo, or conditional rendering
  const [
    engagementStartDate,
    engagementEndDate,
    engagementName,
    selectedClient,
    selectedContact,
    selectedBranch,
    selectedInternalCompany,
    isPreEngagement,
  ] = coreForm.watch([
    'startDate',
    'endDate',
    'engagementName',
    'selectedClient',
    'selectedContact',
    'selectedBranch',
    'selectedInternalCompany',
    'isPreEngagement',
  ])

  // Watch currency from billing form
  const currency = billingForm.watch('selectedCurrency')

  // What a refused save said about each worker, keyed by their profile id, until the next save. Each
  // field's refusal stops showing on its own once something it was refused over changes.
  const [workerSaveErrors, setWorkerSaveErrors] = useState<Record<number, WorkerRefusals>>({})

  // A refused save or submit names the workers it refused over, so the message is split back out onto
  // them and each part sits under the field it is about. A toast alone leaves the reader looking at a
  // list of workers with no idea which one the server meant. Returns what is left for the toast, or null
  // where the error was no refusal.
  //
  // Read off whatever carries a status rather than one error type: the save travels through the
  // engagement client, which wraps it in one of its own.
  const takeRefusal = useCallback((error: unknown): string | null => {
    const refusal = statusOf(error) === 400 && error instanceof Error ? error.message.trim() : null
    const workers = workersForm.getValues('workers') ?? []
    const shown = new Set(workers.map(worker => worker.workerUserProfileId))
    const { byWorker, rest } = refusal
      ? splitRefusal(refusal, id => shown.has(id))
      : { byWorker: {} as RefusedSave['byWorker'], rest: '' }

    const context = { engagementStartDate, engagementEndDate, taskCoverage }
    setWorkerSaveErrors(Object.fromEntries(
      workers
        .filter(worker => byWorker[worker.workerUserProfileId])
        .map(worker => [worker.workerUserProfileId, recordRefusals(byWorker[worker.workerUserProfileId], worker, context)])
    ))

    if (!refusal) {
      return null
    }

    // Whatever named no card still has to be read, so it leads the toast and the pointer to the cards
    // follows it. A refusal that marked nothing red is shown whole: sending the reader to look for a red
    // card there would send them looking for nothing.
    const pointer = Object.keys(byWorker).length > 0
      ? "See the workers marked in red for what is missing."
      : ""

    return [rest, pointer].filter(Boolean).join(" ") || refusal
  }, [workersForm, engagementStartDate, engagementEndDate, taskCoverage])


  // Calculate and set page title (using watched engagementName so it updates)
  const pageTitle = useMemo(() => {
    // Don't set title while still loading
    if (loadingEngagement || !hasInitiallyLoaded) {
      return "Loading Engagement..."
    }

    let title = `Engagement ${getEngagementDisplayId()}`
    if (engagementName) {
      title = `${title} - ${engagementName}`
    }
    else {
      title = `${title} (New)`
    }

    return title
  }, [loadingEngagement, hasInitiallyLoaded, engagementName, version, recordId])
  
  // Set page title (both document title and parent window title)
  usePageTitle(pageTitle)

  // Shared validation logic for submit actions
  const validateForSubmission = useCallback(async (): Promise<boolean> => {
    // Submit all forms to trigger validation and show error indicators
    await submitForms()

    // Check if all forms are valid
    if (!allSectionsComplete) {
      // Find first section with errors and navigate to it
      const firstIncompleteSection = sections
        .map(s => s.id)
        .find(sectionId => !completedSections[sectionId])
      
      if (firstIncompleteSection) {
        goToSection(firstIncompleteSection)
      }

      toast({
        title: "Validation Failed",
        description: "Please fix all validation errors before submitting.",
        variant: "destructive",
      })
      return false
    }
    
    return true
  }, [submitForms, allSectionsComplete, completedSections, sections, goToSection, toast])

  // Handle action functions
  const handleSubmit = useCallback(async () => {
    try {
      if (!await validateForSubmission()) {
        return
      }

      const currentFormState = buildCurrentFormState()
      await handlers.submitEngagement(currentFormState)
      setWorkerSaveErrors({})
      toast({
        title: "Engagement Submitted",
        description: "Engagement has been successfully submitted for review.",
      })
    } catch (error) {
      toast({
        title: "Submit Failed",
        description: takeRefusal(error) ?? (error instanceof Error ? error.message : "Failed to submit engagement"),
        variant: "destructive",
      })
    }
  }, [validateForSubmission, buildCurrentFormState, handlers.submitEngagement, takeRefusal, toast])

  const handleSubmitForReview = useCallback(async () => {
    try {
      if (!await validateForSubmission()) {
        return
      }

      const currentFormState = buildCurrentFormState()
      await handlers.submitEngagementForReview(currentFormState)
      setWorkerSaveErrors({})
      toast({
        title: "Submitted for Review",
        description: "Engagement has been successfully submitted for review.",
      })
    } catch (error) {
      toast({
        title: "Submit for Review Failed",
        description: takeRefusal(error) ?? (error instanceof Error ? error.message : "Failed to submit for review"),
        variant: "destructive",
      })
    }
  }, [validateForSubmission, buildCurrentFormState, handlers.submitEngagementForReview, takeRefusal, toast])

  const handleApprove = useCallback(async () => {
    try {
      await handlers.approveEngagement()
      toast({
        title: "Engagement Approved",
        description: "Engagement has been successfully approved.",
      })
    } catch (error) {
      toast({
        title: "Approval Failed",
        description: error instanceof Error ? error.message : "Failed to approve engagement",
        variant: "destructive",
      })
    }
  }, [handlers.approveEngagement, toast])

  const handleDecline = useCallback(async () => {
    try {
      await handlers.declineEngagement()
      toast({
        title: "Engagement Declined",
        description: "Engagement has been declined and returned to draft status.",
      })
    } catch (error) {
      toast({
        title: "Decline Failed",
        description: error instanceof Error ? error.message : "Failed to decline engagement",
        variant: "destructive",
      })
    }
  }, [handlers.declineEngagement, toast])

  const handleCreateChangeRequest = useCallback(() => {
    setShowCommentDialog(true)
  }, [])

  const handleConfirmChangeRequest = useCallback(async (comment: string) => {
    try {
      setIsSubmittingComment(true)
      const newVersion = await handlers.createChangeRequest(comment)
      
      setShowCommentDialog(false)
      
      toast({
        title: "Change Request Created",
        description: `New version ${newVersion?.version} created successfully.`,
      })
      
      // Navigate to the new change request version
      if (newVersion) {
        navigate(`/${newVersion.recordId}/version/${newVersion.version}`)
      }
    } catch (error) {
      toast({
        title: "Change Request Failed",
        description: error instanceof Error ? error.message : "Failed to create change request",
        variant: "destructive",
      })
      throw error
    } finally {
      setIsSubmittingComment(false)
    }
  }, [handlers.createChangeRequest, navigate, toast])

  const handleDiscard = useCallback(async () => {
    try {
      await handlers.discardEngagement()
      toast({
        title: "Engagement Discarded",
        description: isChangeRequest ? "Change request discarded." : "Draft engagement discarded.",
      })
      
      if (isChangeRequest) {
        // For change requests, redirect to the base engagement (without version)
        navigate(`/${recordId}`)
      } else {
        // For draft engagements, navigate to report screen in parent app
        navigateInParent('/next/report-azsearch/44')
      }
    } catch (error) {
      toast({
        title: "Discard Failed",
        description: error instanceof Error ? error.message : "Failed to discard engagement",
        variant: "destructive",
      })
    }
  }, [handlers.discardEngagement, isChangeRequest, recordId, navigate, toast])

  const handleComplete = useCallback(() => {
    setShowCompleteDialog(true)
  }, [])

  const handleConfirmComplete = useCallback(async (completionDate: Date | null, closedReasonCode: string | null) => {
    try {
      await handlers.completeEngagement(completionDate, closedReasonCode)
      setShowCompleteDialog(false)
      toast({
        title: "Engagement Completed",
        description: "Engagement has been marked as complete.",
      })
    } catch (error) {
      toast({
        title: "Complete Failed",
        description: error instanceof Error ? error.message : "Failed to complete engagement",
        variant: "destructive",
      })
      throw error
    }
  }, [handlers.completeEngagement, toast])

  const fetchMilestoneTransactionInfo = useCallback(async (milestoneReference: string): Promise<MilestoneTransactionInfo> => {
    try {
      const transactions = await engagementApi.getMilestoneTransactions(recordId, version, milestoneReference)
      const nonReversed = filterNonReversedTransactions(transactions)
      
      if (nonReversed.length === 0) {
        return { hasTransactions: false, mostRecent: null }
      }
      
      const mostRecent = getMostRecentTransaction(nonReversed)
      return { hasTransactions: true, mostRecent }
    } catch (error) {
      console.error(`Failed to fetch transaction info for milestone ${milestoneReference}`, error)
      return { hasTransactions: false, mostRecent: null }
    }
  }, [recordId, version, engagementApi])

  const executeTransactionGeneration = useCallback(async (milestoneReference: string) => {
    setGeneratingMilestoneRef(milestoneReference)
    try {
      const result = await handlers.generateMilestoneTransaction(milestoneReference)
      toast({
        title: "Transaction Generated",
        description: `Transaction ${result.transactionNumber} created for milestone ${milestoneReference}.`,
      })
      
      // Refresh transaction info to update UI
      const updatedInfo = await fetchMilestoneTransactionInfo(milestoneReference)
      setMilestoneTransactionInfo(prev => ({ ...prev, [milestoneReference]: updatedInfo }))
    } catch (error) {
      toast({
        title: "Transaction Failed",
        description: error instanceof Error ? error.message : "Failed to generate milestone transaction",
        variant: "destructive",
      })
    } finally {
      setGeneratingMilestoneRef(null)
    }
  }, [handlers.generateMilestoneTransaction, fetchMilestoneTransactionInfo, toast])

  const handleGenerateMilestoneTransaction = useCallback(async (milestoneReference: string, amount: number) => {
    try {
      // Fetch existing transactions for this milestone
      const existingTransactions = await engagementApi.getMilestoneTransactions(recordId, version, milestoneReference)
      
      // Check if any transactions match the amount
      const nonReversed = filterNonReversedTransactions(existingTransactions)
      const duplicates = nonReversed.filter(tx => tx.subtotal === amount)
      
      if (duplicates.length > 0) {
        // Show duplicate warning dialog
        setDuplicateTransactions(duplicates)
        setPendingMilestoneGeneration({ reference: milestoneReference, amount })
        setShowDuplicateDialog(true)
        return
      }
      
      // No duplicates, proceed with generation
      await executeTransactionGeneration(milestoneReference)
    } catch (error) {
      toast({
        title: "Transaction Failed",
        description: error instanceof Error ? error.message : "Failed to check for duplicate transactions",
        variant: "destructive",
      })
    }
  }, [recordId, version, engagementApi, executeTransactionGeneration, toast])
  
  const handleDuplicateDialogContinue = useCallback(async () => {
    if (!pendingMilestoneGeneration) return
    
    await executeTransactionGeneration(pendingMilestoneGeneration.reference)
    setPendingMilestoneGeneration(null)
    setDuplicateTransactions([])
  }, [pendingMilestoneGeneration, executeTransactionGeneration])
  
  // Watch milestones form array using useWatch (proper pattern for react-hook-form arrays)
  // This properly detects changes to the form array without reference instability issues
  const milestones = useWatch({
    control: milestonesForm.control,
    name: 'milestones',
    defaultValue: []
  })

  // Create a stable list of completed milestone references from saved backend data
  // Only fetches transaction info for milestones that are completed in the backend
  const completedMilestoneRefs = useMemo(() => {
    const savedMilestones = formState?.milestones || []
    
    // Only include completed milestones from saved backend data
    return savedMilestones
      .filter(m => m.status === MILESTONE_STATUS.COMPLETED && m.reference)
      .map(m => m.reference!)
  }, [formState?.milestones])

  // Convert to string for stable comparison (array references always differ even with same content)
  const completedMilestoneRefsKey = completedMilestoneRefs.join(',')
  
  // Group related props into context objects to reduce prop drilling and improve clarity
  const revenueRecognitionContext = useMemo(() => ({
    engagementStartDate,
    engagementEndDate,
    engagementName,
    selectedClient,
    organizations: references.organizations,
  }), [engagementStartDate, engagementEndDate, engagementName, selectedClient, references.organizations])
  
  const milestoneActionsContext = useMemo(() => ({
    onAdd: handleMilestoneAdd,
    onRemove: handleMilestoneRemove,
    onGenerateTransaction: handleGenerateMilestoneTransaction,
    canGenerateTransaction: Boolean(formState?.actionContext?.canGenerateMilestoneTransaction),
    isHandlingAction,
    currentAction,
  }), [handleMilestoneAdd, handleMilestoneRemove, handleGenerateMilestoneTransaction, formState?.actionContext?.canGenerateMilestoneTransaction, isHandlingAction, currentAction])

  // Handle saving engagement data
  const handleSave = useCallback(async () => {
    try {
      const currentFormState = buildCurrentFormState()
      await handlers.saveEngagement(currentFormState)
      setWorkerSaveErrors({})
      toast({
        title: "Engagement Saved",
        description: "Changes have been saved successfully.",
      })
    } catch (error) {
      const refused = takeRefusal(error)

      toast({
        title: refused != null ? "Invalid save" : "Save Failed",
        description: refused ?? (error instanceof Error ? error.message : "Failed to save engagement"),
        variant: "destructive",
      })
      throw error // Re-throw to allow callers to handle the error
    }
  }, [buildCurrentFormState, handlers.saveEngagement, takeRefusal, toast])

  // Build actions object (page owns this structure)
  const actions = useMemo(() => ({
    refresh: {
      canExecute: capabilities.canSave,
      handler: handlers.refreshData
    },
    save: {
      canExecute: capabilities.canSave,
      handler: handleSave
    },
    submit: {
      canExecute: capabilities.canSubmit,
      handler: handleSubmit
    },
    submitForReview: {
      canExecute: capabilities.canSubmitForReview,
      handler: handleSubmitForReview
    },
    discard: {
      canExecute: capabilities.canDiscard,
      handler: handleDiscard
    },
    approve: {
      canExecute: capabilities.canApprove,
      handler: handleApprove
    },
    decline: {
      canExecute: capabilities.canDecline,
      handler: handleDecline
    },
    createChangeRequest: {
      canExecute: capabilities.canCreateChangeRequest,
      handler: handleCreateChangeRequest
    },
    recall: {
      canExecute: capabilities.canRecall,
      handler: handlers.recallEngagement
    },
    recallToAccounting: {
      canExecute: capabilities.canRecallToAccounting,
      handler: handlers.recallEngagementToAccounting
    },
    complete: {
      canExecute: capabilities.canComplete,
      handler: handleComplete
    },
    reactivate: {
      canExecute: capabilities.canReactivate,
      handler: handlers.reactivateEngagement
    },
  }), [capabilities, handlers, handleSave, handleSubmit, handleSubmitForReview, handleApprove, handleDecline, handleDiscard, handleCreateChangeRequest, handleComplete])

  // Track when initial loading is complete
  useEffect(() => {
    if (!loadingEngagement && isFormsPopulated && codeValuesReady && engagementLookupsReady && dictionary && !hasInitiallyLoaded) {
      setHasInitiallyLoaded(true)
    }
  }, [loadingEngagement, isFormsPopulated, codeValuesReady, engagementLookupsReady, dictionary, hasInitiallyLoaded])

  // Load milestone transaction info when completed milestones list changes
  // Using stable string key prevents infinite loops from object reference changes
  useEffect(() => {
    //canGenerateMilestoneTransaction engagement level condition
    if (!hasInitiallyLoaded || completedMilestoneRefs.length === 0 || !formState?.actionContext?.canGenerateMilestoneTransaction) {
      // Clear transaction info when conditions aren't met (prevents stale data)
      setMilestoneTransactionInfo({})
      return
    }
    
    const loadInfo = async () => {
      const infoMap: Record<string, MilestoneTransactionInfo> = {}
      await Promise.all(
        completedMilestoneRefs.map(async (reference) => {
          const info = await fetchMilestoneTransactionInfo(reference)
          infoMap[reference] = info
        })
      )
      setMilestoneTransactionInfo(infoMap)
    }
    
    loadInfo()
  }, [hasInitiallyLoaded, completedMilestoneRefsKey, recordId, version, formState?.actionContext?.canGenerateMilestoneTransaction])

  // Handle engagement load failures BEFORE the loading gate. hasInitiallyLoaded only flips
  // true once the forms populate, which never happens when there's no data — so checking this
  // after the loader would spin forever. Two shapes: access/missing (gated copy, no retry)
  // vs transient (dynamic message + retry).
  if (engagementError) {
    if (errorIsAccessOrMissing) {
      return (
        <div className="px-6 py-6" role="alert">
          <div className="flex items-center justify-center gap-6 rounded-md bg-gray-100 px-6 py-8">
            <AlertCircle aria-hidden="true" className="h-12 w-12 shrink-0 text-gray-500" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {dictionary?.engagement?.errors?.cannotOpen?.title || "Can't open this engagement"}
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                {dictionary?.engagement?.errors?.cannotOpen?.body ||
                  "It may not exist, or you may not have permission to view it."}
              </p>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="px-6 py-6" role="alert">
        <div className="flex items-center justify-center gap-6 rounded-md bg-gray-100 px-6 py-8">
          <AlertCircle aria-hidden="true" className="h-12 w-12 shrink-0 text-gray-500" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {dictionary?.engagement?.errors?.loadFailed?.title || "Failed to load engagement"}
            </h2>
            <p className="mt-1 text-sm text-gray-600">{engagementError}</p>
            <Button onClick={handlers.refreshData} className="mt-3" variant="outline">
              {dictionary?.engagement?.errors?.loadFailed?.tryAgain || "Try Again"}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // Show full-page loader only for initial load
  if (!hasInitiallyLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>
            {!dictionary ? 'Loading interface...' :
             loadingEngagement ? 'Loading engagement data...' : 'Loading...'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Version Change Loading Overlay */}
      {loadingEngagement && hasInitiallyLoaded && (
        <div className="fixed inset-0 bg-black/20 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg border p-6 flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
            <span className="text-sm">Loading version...</span>
          </div>
        </div>
      )}

      {/* Outer container with horizontal padding - contains header border */}
      <div className="px-6">
        {/* Header with bottom border - border stays within px-6 */}
        <header className="py-4 border-b">
          {/* Breadcrumb */}
          <div className="flex items-center space-x-2 text-slate-500 text-sm mb-3">
              <ParentNavigationLink route="/next/report-azsearch/44" className="hover:text-slate-900">
                Engagements List
              </ParentNavigationLink>
            <ChevronRight className="h-3 w-3" />
              <span className="text-slate-900 font-medium">Engagement</span>
            </div>
          {/* Title */}
          <h1 className="text-2xl font-semibold text-slate-900">
            {navigationMode === "setup" 
              ? `Engagement ${getEngagementDisplayId()} Setup`
              : `Engagement ${getEngagementDisplayId()}`
            }
          </h1>
        </header>
        </div>

      {/* Main content - same horizontal padding, vertical spacing between children */}
      <main className="px-6 py-6 space-y-6">
        
        {/* Section 1: Title + Version + Actions */}
        <EngagementToolbar
          formState={formState}
          navigationMode={navigationMode}
          version={version}
          recordId={recordId}
          engagementName={coreForm.getValues('engagementName')}
          isPreEngagement={isPreEngagement === true}
          allVersions={allVersions}
          allSectionsComplete={allSectionsComplete}
          canViewPayRates={capabilities.canViewWorkerPayRates}
          isHandlingAction={isHandlingAction}
          currentAction={currentAction}
          actions={actions}
          confirmNavigation={confirmNavigation}
        />
        
        {/* Details Content */}
        {activeTab === ENGAGEMENT_TABS.DETAILS && (
          <>
            {/* Stepper (when setup) */}
            {navigationMode === 'setup' && (
              <FormStepper
                completedSections={completedSections}
                hiddenSections={hiddenSections}
              />
            )}

            {/* Tab container */}
            <Tabs 
              value={activeSection || undefined} 
              onValueChange={goToSection}
              className={navigationMode === 'setup' ? 'bg-gray-100' : ''}
            >
              {/* FormTabs (TabsList, when existing) */}
              {navigationMode === 'existing' && (
                <FormTabs
                  completedSections={completedSections}
                  hiddenSections={hiddenSections}
                />
              )}

              {/* Engagement Header (shown above all tab content) */}
              {navigationMode === "setup" && formState && (
                <div className="p-6 pb-0">
                  <EngagementHeader
                    selectedClient={selectedClient}
                    selectedContact={selectedContact}
                    selectedBranch={selectedBranch}
                    engagementType={engagementType}
                    organizations={references.organizations}
                    profileSummaries={references.profiles}
                  />
                </div>
              )}

              {/* Core Section */}
              <TabsContent value="core">
                <CoreSection
                  form={coreForm}
                  isCompleted={completedSections["core"]}
                  isReadonly={isFormReadonly}
                  isChangeRequest={isChangeRequest}
                  canEditEngagementSource={capabilities.canEditEngagementSource}
                  currentRecordId={recordId}
                  hiddenSections={hiddenSections}
                  profileSummaries={references.profiles}
                  organizations={references.organizations}
                  sourceEngagements={references.engagements}
                  onCopyFromOriginal={handleCopyFromOriginal}
                  isCopying={isCopying}
                />
              </TabsContent>

              {/* Billing Section */}
              <TabsContent value="billing">
                <BillingSetupSection
                  form={billingForm}
                  milestoneForm={milestonesForm}
                  revenueRecognitionForm={revenueRecognitionForm}
                  milestoneActions={milestoneActionsContext}
                  milestoneTransactionInfo={milestoneTransactionInfo}
                  generatingMilestoneRef={generatingMilestoneRef}
                  isCompleted={completedSections["billing"]}
                  hiddenSections={hiddenSections}
                  isReadonly={isFormReadonly}
                  isChangeRequest={isChangeRequest}
                  engagementType={engagementType}
                  currency={currency}
                  revenueRecognitionContext={revenueRecognitionContext}
                  canLockRevenueRecognition={capabilities.canLockRevenueRecognition}
                />
              </TabsContent>

              {/* Workers Section */}
              <TabsContent value="workers">
                <WorkersSection
                  form={workersForm}
                  onWorkerAdd={handleWorkerAdd}
                  onWorkerEdit={handleWorkerEdit}
                  onWorkerRemove={handleWorkerRemove}
                  isCompleted={completedSections["workers"]}
                  isReadonly={isFormReadonly}
                  billingType={billingType}
                  engagementType={engagementType}
                  engagementStatus={formState?.actionContext?.engagement?.status || undefined}
                  engagementRecordId={recordId}
                  previousWorkerIds={previousWorkerIds}
                  engagementStartDate={engagementStartDate}
                  engagementEndDate={engagementEndDate}
                  currency={currency}
                  hiddenSections={hiddenSections}
                  canViewPayRates={capabilities.canViewWorkerPayRates}
                  workerProfiles={references.profiles}
                  taskCoverage={taskCoverage}
                  workerSaveErrors={workerSaveErrors}
                />
              </TabsContent>

              {/* Timesheet Section */}
              <TabsContent value="timesheet">
                <TimesheetAndExpenseSection
                  form={timesheetAndExpenseForm}
                  isCompleted={completedSections["timesheet"]}
                  isReadonly={isFormReadonly}
                  selectedInternalCompany={selectedInternalCompany}
                  onApproverAdd={handleApproverAdd}
                  onApproverRemove={handleApproverRemove}
                  onExpenseApproverAdd={handleExpenseApproverAdd}
                  onExpenseApproverRemove={handleExpenseApproverRemove}
                  hiddenSections={hiddenSections}
                  profileSummaries={references.profiles}
                />
              </TabsContent>

              {/* Tasks Section */}
              <TabsContent value="tasks">
                <TasksSection
                  form={tasksForm}
                  onTaskAdd={handleTaskAdd}
                  onTaskEdit={handleTaskEdit}
                  onTaskRemove={handleTaskRemove}
                  isCompleted={completedSections["tasks"]}
                  isReadonly={isFormReadonly}
                  isChangeRequest={isChangeRequest}
                  engagementType={engagementType}
                  billingType={billingType}
                  hiddenSections={hiddenSections}
                />
              </TabsContent>

              {/* Invoice Section */}
              {!hiddenSections["invoice"] && (
                <TabsContent value="invoice">
                  <InvoiceSection
                    form={invoiceForm}
                    selectedClient={selectedClient}
                    isCompleted={completedSections["invoice"]}
                    isReadonly={isFormReadonly}
                    hiddenSections={hiddenSections}
                    billingType={billingType}
                    onNoteAdd={handleInvoiceNoteAdd}
                    onNoteRemove={handleInvoiceNoteRemove}
                    profileSummaries={references.profiles}
                  />
                </TabsContent>
              )}

              {/* Documents Section */}
              <TabsContent value="documents">
                <DocumentsSection
                  isCompleted={completedSections["documents"]}
                  recordId={recordId}
                  isReadonly={isFormReadonly}
                  hiddenSections={hiddenSections}
                  nextButtonOverride={
                    (capabilities.canSubmit || capabilities.canSubmitForReview) ? (
                      <Button
                        onClick={capabilities.canSubmitForReview ? handleSubmitForReview : handleSubmit}
                        disabled={isHandlingAction}
                        variant="ghost"
                        size="default"
                        className="flex items-center gap-3 px-6 py-1.5 text-base font-medium text-primary hover:text-primary/80"
                      >
                        {currentAction === ENGAGEMENT_ACTIONS.SUBMIT_FOR_REVIEW || currentAction === ENGAGEMENT_ACTIONS.SUBMIT
                          ? getActionLoadingLabel(capabilities.canSubmitForReview ? ENGAGEMENT_ACTIONS.SUBMIT_FOR_REVIEW : ENGAGEMENT_ACTIONS.SUBMIT, dictionary)
                          : getActionLabel(capabilities.canSubmitForReview ? ENGAGEMENT_ACTIONS.SUBMIT_FOR_REVIEW : ENGAGEMENT_ACTIONS.SUBMIT, dictionary)
                        }
                      </Button>
                    ) : undefined
                  }
                />
              </TabsContent>
            </Tabs>
          </>
        )}

        {/* Overview Content */}
        {activeTab === ENGAGEMENT_TABS.OVERVIEW && (
            <TransactionsContainer 
            engagementId={recordId}
          />
        )}

      </main>
      
      {/* Duplicate Transaction Warning Dialog */}
      <DuplicateTransactionWarningDialog
        open={showDuplicateDialog}
        onOpenChange={setShowDuplicateDialog}
        onContinue={handleDuplicateDialogContinue}
        existingTransactions={duplicateTransactions}
      />

      {/* Comment Dialog */}
      <CommentDialog
        open={showCommentDialog}
        onOpenChange={setShowCommentDialog}
        onConfirm={handleConfirmChangeRequest}
        isSubmitting={isSubmittingComment}
        title="Edit Engagement"
        description="A new draft version will be created for your changes."
        label="Reason"
        placeholder="Enter reason for changes..."
      />

      {/* Complete Engagement Dialog */}
      <CompleteEngagementDialog
        open={showCompleteDialog}
        onOpenChange={setShowCompleteDialog}
        onConfirm={handleConfirmComplete}
        currentClosedDate={coreForm.getValues('closedDate')}
        currentClosedReason={coreForm.getValues('closedReason')}
        startDate={coreForm.getValues('startDate')}
        endDate={coreForm.getValues('endDate')}
      />
    </div>
  )
}

export default function EngagementDetails() {
  const { dictionary } = useLanguage()
  const { id, version } = useParams<{ id: string; version?: string }>()

  // Code values hook - loads static code values on app boot  
  const { isReady: codeValuesReady } = useCodeValues()

  // Memoize sections array to prevent recreating on every render
  const sections = useMemo(() => [
    {
      id: "core",
      label: dictionary?.engagement?.sections?.core || "Core Information",
      icon: <LayoutDashboard className="h-4 w-4" />,
    },
    {
      id: "billing",
      label: dictionary?.engagement?.sections?.billing || "Billing setup",
      icon: <Receipt className="h-4 w-4" />,
    },
    {
      id: "workers",
      label: dictionary?.engagement?.sections?.workers || "Worker setup",
      icon: <Users className="h-4 w-4" />,
    },
    {
      id: "timesheet",
      label: dictionary?.engagement?.sections?.timesheet || "Timesheet and Expense setup",
      icon: <Clock className="h-4 w-4" />,
    },
    { id: "tasks", label: dictionary?.engagement?.sections?.tasks || "Tasks", icon: <ListTodo className="h-4 w-4" /> },
    {
      id: "invoice",
      label: dictionary?.engagement?.sections?.invoice || "Invoice setup",
      icon: <FileText className="h-4 w-4" />,
    },
    {
      id: "documents",
      label: dictionary?.engagement?.sections?.documents || "Documents",
      icon: <Files className="h-4 w-4" />,
    },
  ], [dictionary])

  return (
    <EngagementNavigationProvider
      sections={sections}
      engagementKey={`${id}-${version || 'base'}`}
    >
      <EngagementDetailsContent />
    </EngagementNavigationProvider>
  )
}
